// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { Dropdown } from './Dropdown';
import {
  TablePaginationFirstIcon,
  TablePaginationPrevIcon,
  TablePaginationNextIcon,
  TablePaginationLastIcon,
} from './TablePaginationIcon';
import './TablePagination.css';

export interface TablePaginationConfig {
  /** 1-indexed current page (display value shown to the user). */
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  itemsPerPageOptions?: number[];
  /** Receives the new 1-indexed page number. */
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (size: number) => void;
}

export interface TablePaginationProps {
  config: TablePaginationConfig;
}

/**
 * Compact pagination row for tables: "Items per page: N", "X of Y" and four
 * navigation buttons (first / previous / next / last). Designed to live at
 * the bottom of a `LOATableCard`-style card; uses Roboto 12px tokens and
 * the `Dropdown` `inline` variant so it reads as plain inline text.
 *
 * Pages are **1-indexed**. Call sites consuming a 0-indexed Spring `Page`
 * DTO should pass `currentPage={page + 1}` and translate back inside
 * `onPageChange`.
 */
export function TablePagination({ config }: TablePaginationProps) {
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    itemsPerPageOptions = [5, 10, 20],
    onPageChange,
    onItemsPerPageChange,
  } = config;

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages || totalPages === 0;

  return (
    <div className="fin-table-pagination">
      <div className="fin-table-pagination__info">
        <span>Items per page:</span>
        <Dropdown
          variant="inline"
          options={itemsPerPageOptions.map((opt) => ({
            value: String(opt),
            text: String(opt),
          }))}
          value={String(itemsPerPage)}
          onChange={(value) => {
            onItemsPerPageChange?.(Number(value));
            onPageChange(1);
          }}
        />
        <span className="fin-table-pagination__page-info">
          {currentPage} of {totalPages}
        </span>
      </div>
      <div className="fin-table-pagination__controls">
        <button
          type="button"
          className="fin-table-pagination__btn"
          onClick={() => onPageChange(1)}
          disabled={isFirst}
          aria-label="First page"
        >
          <TablePaginationFirstIcon />
        </button>
        <button
          type="button"
          className="fin-table-pagination__btn"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={isFirst}
          aria-label="Previous page"
        >
          <TablePaginationPrevIcon />
        </button>
        <button
          type="button"
          className="fin-table-pagination__btn"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={isLast}
          aria-label="Next page"
        >
          <TablePaginationNextIcon />
        </button>
        <button
          type="button"
          className="fin-table-pagination__btn"
          onClick={() => onPageChange(totalPages)}
          disabled={isLast}
          aria-label="Last page"
        >
          <TablePaginationLastIcon />
        </button>
      </div>
    </div>
  );
}
